import styled, { css } from 'styled-components';
import * as fonts from 'view/styles/fonts';
import * as grid from 'view/styles/grid';
import * as mixins from 'view/styles/mixins';
import * as elevation from 'view/styles/elevation';
import * as zIndex from 'view/styles/z-index';
import * as transitions from 'view/styles/transitions';
import * as bp from 'view/styles/breakpoints';
import { HtmlButton } from 'view/common/analytics/html-button';
import { MODAL_EXPAND_TRANSITION_MS } from '../styled';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';

export const SEARCH_BAR_HEIGHT = grid.unitless * 11;
export const GUTTER = grid.unitless * 2;
export const SEARCH_INPUT_FONT_SIZE = fonts.mediumLarge;
const INPUT_HEIGHT = grid.unitless * 7;
const BUTTON_HEIGHT = grid.unitless * 6;

export const Root = styled.div`
    ${mixins.borderBox};
    ${mixins.flexCenter};
    height: ${SEARCH_BAR_HEIGHT}px;

    justify-content: space-around;
    padding: 0 ${GUTTER}px;
    position: relative;
`;

export const Form = styled.form`
    margin: 0;
    width: 100%;
`;

export const Input = styled.input`
    ${SEARCH_INPUT_FONT_SIZE};
    ${mixins.borderRadius};
    width: 100%;
    height: ${INPUT_HEIGHT}px;
    margin: 0 auto;
    padding: 0 ${GUTTER}px;
    border: 0;
    outline: none;
    font-weight: inherit;
    box-shadow: none;
    color: ${token('color.text.subtle', colors.N800)};
    background-color: ${token('color.background.input', colors.N0)};
    position: relative;
    z-index: ${zIndex.searchBackground + 1};

    /* IE 11 fixes */
    ${mixins.hideIECross};
    ${mixins.placeholder(token('color.text.subtlest', colors.N200))};
`;

export const InputBackground = styled.div<{ term: string }>`
    &::before {
        ${elevation.e300};
        ${transitions.fadeInOutTiming(MODAL_EXPAND_TRANSITION_MS)};
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        /* This should only be shown if term is truthy. */
        opacity: 0;
        /* This pseudo element should not be interactive! Else it blocks clicks to the close button and input. */
        pointer-events: none;
    }

    ${(props) =>
        mixins.applyIf(
            !props.term,
            css`
                &::before {
                    opacity: 1;
                }
            `
        )};
    position: absolute;
    height: ${SEARCH_BAR_HEIGHT}px;
    left: 0;
    right: 0;
    top: 0;
    z-index: ${zIndex.searchBackground};
    border-bottom: 1px solid ${token('color.border', colors.N30)};
    background-color: ${token('elevation.surface.overlay', colors.N0)};
`;

export const Button = styled(HtmlButton)`
    ${mixins.focusRing.default};
    height: ${BUTTON_HEIGHT}px;
    width: ${BUTTON_HEIGHT}px;
    cursor: pointer;
    background: transparent;
    border: none;
    padding: 0;
    position: relative;

    ${bp.fromXSmall.css`
        ${mixins.centerVertically('absolute')};
        right: ${GUTTER}px;
    `};
`;

export const HiddenLabel = styled.label`
    ${mixins.hideVisually};
`;

export const BackButtonContainer = styled.div`
    position: absolute;
    left: ${GUTTER}px;
    ${bp.onlyMobile.css`
        margin-left: ${token('space.100', '8px')};
    `};
`;

export const SearchTextField = styled(Textfield)`
    padding-left: ${token('space.025', '2px')};
    height: ${token('space.400', '32px')};
`;

export const Icon = styled.span`
    margin-left: ${token('space.100', '8px')};
`;
